import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Link } from 'react-router-dom'
import { type Language, type Currency } from './LandingPage'
import { monthlyPricing, type PlanId } from '@/lib/pricing'
import { PLAN_ENTITLEMENTS, AI_LIMIT_PER_SEAT } from '@/lib/plan-entitlements'

interface PricingSectionProps {
  language: Language
  currency: Currency
}

const content = {
  pt: {
    title: 'Pricing completo na home, sem promessas fora do produto.',
    subtitle: 'Planos sincronizados com pricing real, limites atuais e waitlist disponível a partir do Pro.',
    ctaFree: 'Criar conta gratuitamente',
    ctaPaid: 'Começar com {{plan}}',
    perMonth: '/mês',
    tags: {
      popular: 'Mais escolhido',
      enterprise: 'Escala',
      recurring: 'Recorrente',
      oneOff: 'One-off',
    },
    labels: {
      events: 'eventos/mês',
      proposals: 'propostas/mês',
      packages: 'pacotes',
      unlimitedEvents: 'Eventos ilimitados',
      unlimitedProposals: 'Propostas ilimitadas',
      unlimitedPackages: 'Pacotes ilimitados',
      ai: 'IA',
      perSeat: 'por assento',
      users: 'Usuários',
      addOns: 'Add-ons',
      waitlist: 'Waitlist',
      yes: 'sim',
      no: 'não',
    },
  },
  en: {
    title: 'Full pricing on the homepage, with no promises outside the product.',
    subtitle: 'Plans synced with real pricing, current limits, and waitlist available from Pro upward.',
    ctaFree: 'Create account for free',
    ctaPaid: 'Start with {{plan}}',
    perMonth: '/month',
    tags: {
      popular: 'Most chosen',
      enterprise: 'Scale',
      recurring: 'Recurring',
      oneOff: 'One-off',
    },
    labels: {
      events: 'events/month',
      proposals: 'proposals/month',
      packages: 'packages',
      unlimitedEvents: 'Unlimited events',
      unlimitedProposals: 'Unlimited proposals',
      unlimitedPackages: 'Unlimited packages',
      ai: 'AI',
      perSeat: 'per seat',
      users: 'Users',
      addOns: 'Add-ons',
      waitlist: 'Waitlist',
      yes: 'yes',
      no: 'no',
    },
  },
  es: {
    title: 'Pricing completo en la home, sin promesas fuera del producto.',
    subtitle: 'Planes sincronizados con precios reales, límites actuales y lista de espera disponible desde Pro.',
    ctaFree: 'Crear cuenta gratis',
    ctaPaid: 'Empezar con {{plan}}',
    perMonth: '/mes',
    tags: {
      popular: 'Más elegido',
      enterprise: 'Escala',
      recurring: 'Recurrente',
      oneOff: 'One-off',
    },
    labels: {
      events: 'eventos/mes',
      proposals: 'propuestas/mes',
      packages: 'paquetes',
      unlimitedEvents: 'Eventos ilimitados',
      unlimitedProposals: 'Propuestas ilimitadas',
      unlimitedPackages: 'Paquetes ilimitados',
      ai: 'IA',
      perSeat: 'por asiento',
      users: 'Usuarios',
      addOns: 'Add-ons',
      waitlist: 'Waitlist',
      yes: 'sí',
      no: 'no',
    },
  },
}

function formatPlanPrice(planId: PlanId, currency: Currency): string {
  const price = monthlyPricing[planId][currency.toLowerCase() as 'usd' | 'brl' | 'eur']
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US'

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(price)
}

function planLines(plan: PlanId, language: Language) {
  const entitlement = PLAN_ENTITLEMENTS[plan]
  const labels = content[language].labels
  const usersLabel = language === 'pt'
    ? entitlement.usersLabel
    : language === 'es'
      ? plan === 'free' || plan === 'starter'
        ? '1 usuario'
        : plan === 'pro'
          ? '3 usuarios'
          : '10+ usuarios'
      : plan === 'free' || plan === 'starter'
        ? '1 user'
      : plan === 'pro'
        ? '3 users'
        : '10+ users'

  return [
    `${entitlement.maxEvents >= 999999 ? labels.unlimitedEvents : `${entitlement.maxEvents} ${labels.events}`}`,
    `${entitlement.maxProposals >= 999999 ? labels.unlimitedProposals : `${entitlement.maxProposals} ${labels.proposals}`}`,
    `${entitlement.maxPackages >= 999999 ? labels.unlimitedPackages : `${entitlement.maxPackages} ${labels.packages}`}`,
    `${labels.ai}: ${entitlement.aiTokensMonthly === AI_LIMIT_PER_SEAT ? labels.perSeat : entitlement.aiTokensMonthly}`,
    `${labels.users}: ${usersLabel}`,
    `${labels.addOns}: ${entitlement.canUseAddOns ? labels.yes : labels.no}`,
    `${labels.waitlist}: ${entitlement.canUseWaitlist ? labels.yes : labels.no}`,
  ]
}

export function PricingSection({ language, currency }: PricingSectionProps) {
  const t = content[language]
  const plans: Array<{ id: PlanId; name: string; badge?: string }> = [
    { id: 'free', name: 'Free' },
    { id: 'starter', name: 'Starter' },
    { id: 'pro', name: 'Pro', badge: t.tags.popular },
    { id: 'studio', name: 'Studio', badge: t.tags.enterprise },
  ]

  return (
    <section id="pricing" className="bg-[#0b2a4a] px-6 py-20 text-[#f6ebdd]">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">{t.title}</h2>
          <p className="mt-4 text-lg text-[#f6ebdd]/72">{t.subtitle}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-4">
          {plans.map((plan) => (
            <article
              key={plan.id}
              className={`rounded-[30px] border p-7 shadow-[0_30px_80px_rgba(0,0,0,0.16)] ${
                plan.id === 'pro'
                  ? 'border-[#f08a24] bg-[#f6ebdd] text-[#0b2a4a]'
                  : 'border-white/10 bg-white/6'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-2xl font-semibold">{plan.name}</h3>
                  <p className="mt-3 text-4xl font-semibold">
                    {formatPlanPrice(plan.id, currency)}
                    <span className="ml-1 text-base font-medium opacity-70">{t.perMonth}</span>
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {plan.badge && <Badge className="bg-[#f08a24] text-white">{plan.badge}</Badge>}
                  <Badge variant="outline" className={plan.id === 'pro' ? 'border-[#0b2a4a]/14 text-[#0b2a4a]' : 'border-white/14 text-[#f6ebdd]'}>
                    {t.tags.oneOff}
                  </Badge>
                  {(plan.id === 'pro' || plan.id === 'studio') && (
                    <Badge variant="outline" className={plan.id === 'pro' ? 'border-[#0b2a4a]/14 text-[#0b2a4a]' : 'border-white/14 text-[#f6ebdd]'}>
                      {t.tags.recurring}
                    </Badge>
                  )}
                </div>
              </div>

              <ul className="mt-8 space-y-3 text-sm leading-7">
                {planLines(plan.id, language).map((line) => (
                  <li key={line} className="border-b border-current/10 pb-3 last:border-b-0 last:pb-0">
                    {line}
                  </li>
                ))}
              </ul>

              <Link to={`/${language}/register?plan=${plan.id}&currency=${currency}&from=landing_pricing`} className="mt-8 block">
                <Button
                  className={`w-full ${
                    plan.id === 'pro'
                      ? 'bg-[#0b2a4a] text-[#f6ebdd] hover:bg-[#0b2a4a]/92'
                      : 'bg-[#f08a24] text-white hover:bg-[#f08a24]/90'
                  }`}
                >
                  {plan.id === 'free' ? t.ctaFree : t.ctaPaid.replace('{{plan}}', plan.name)}
                </Button>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
